import { memo, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { OEContext } from '@aytm/app/stats/page/comments'

import { CORRECTIONS, ORIGINALS, CORRECTED_SPELLING } from '@aytm/app/stats/page/comments/lib/constants'
import { DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/components/lib/constants'

import { pressable } from '@aytm/app/stats/components/lib/pressable'

import { Checkbox } from 'primereact/checkbox'
import { RadioButton } from 'primereact/radiobutton'

import ClickOutside from '@aytm/lib/rfd-ui/components/basic/click-outside'
import Dropdown from '@aytm/lib/rfd-ui/components/basic/dropdown'

import style from '@aytm/app/stats/page/comments/styles/dropdown.module.scss'

const SurveyDropdown = memo(
  ({
    questionsList,
    responseOrderRef,
    opened,
    hasQuestionOrder,
    isAdvancedFiltersDisabled,
    onNewFilter,
    onToggleQAMode,
    onClickOutside
  }) => {
    const { searchState, dispatch } = useContext(OEContext)

    const {
      request: { selectedQuestion, query },
      correctionsLevel,
      totalComments,
      totalImageResponses,
      totalVideoResponses,
      isHighlightSearchTerms,
      isResponseOrder,
      isSentimentsManagementDisabled,
      isShowCorrectionsDisabled,
      isShowSentiments,
      isQAModeDisabled,
      isVideoResponsesMode
    } = searchState

    const question = useMemo(
      () => questionsList.find(q => q.value == selectedQuestion) || null,
      [questionsList, selectedQuestion]
    )

    const isCreateFilterDisabled = useMemo(
      () =>
        (!query.length && selectedQuestion == DEFAULT_SELECTED_QUESTION) ||
        query.some(item => 'batch' == item.type) ||
        false,
      [query, selectedQuestion]
    )

    const onToggleCorrectionLevel = level =>
      dispatch({ type: 'SET_PARAM', request: { name: 'correctionsLevel', value: level } })

    const onToggleResponseOrder = isResponseOrder => {
      dispatch({ type: 'SET_PARAM', request: { name: 'isResponseOrder', value: isResponseOrder } })
      Object.keys(responseOrderRef.current).forEach(key => {
        responseOrderRef.current[key](isResponseOrder)
      })
    }

    const onToggleHighlightSearchTerms = () =>
      dispatch({ type: 'SET_PARAM', request: { name: 'isHighlightSearchTerms', value: !isHighlightSearchTerms } })

    const onToggleShowSentiment = () =>
      dispatch({
        type: 'SET_PARAM',
        request: { name: 'isShowSentiments', value: !isShowSentiments }
      })

    return (
      <Dropdown
        data-testid="stat-comments-settings"
        className={style['settings-dropdown']}
        width="auto"
        align="right"
        opened={opened}
      >
        {() => (
          <ClickOutside onClickOutside={() => setTimeout(() => onClickOutside())}>
            {/* a named group of rows, not a list or a menu: its radios, checkboxes and actions are the controls,
                which a list item or a menu entry may not be or hold */}
            <div className={`${style['settings-dropdown-options']} p-mod-small`} role="group" aria-label="All tools">
              {isShowCorrectionsDisabled ? null : (
                <div role="radiogroup" aria-label="Spelling corrections">
                  <div
                    data-testid="stat-comments-show-originals"
                    className={style['settings-dropdown-option']}
                    onClick={() => onToggleCorrectionLevel(ORIGINALS)}
                  >
                    <RadioButton
                      checked={correctionsLevel == ORIGINALS}
                      name="stat-comments-corrections-level"
                      aria-labelledby="stat-comments-originals-label"
                    />
                    <span id="stat-comments-originals-label" className={style['settings-dropdown-option-label']}>
                      Originals
                    </span>
                  </div>
                  <div
                    data-testid="stat-comments-show-corrections"
                    className={style['settings-dropdown-option']}
                    onClick={() => onToggleCorrectionLevel(CORRECTIONS)}
                  >
                    <RadioButton
                      checked={correctionsLevel == CORRECTIONS}
                      name="stat-comments-corrections-level"
                      aria-labelledby="stat-comments-corrections-label"
                    />
                    <span id="stat-comments-corrections-label" className={style['settings-dropdown-option-label']}>
                      Show corrections
                    </span>
                  </div>
                  <div
                    data-testid="stat-comments-corrected-spelling"
                    className={style['settings-dropdown-option']}
                    onClick={() => onToggleCorrectionLevel(CORRECTED_SPELLING)}
                  >
                    <RadioButton
                      checked={correctionsLevel == CORRECTED_SPELLING}
                      name="stat-comments-corrections-level"
                      aria-labelledby="stat-comments-corrected-spelling-label"
                    />
                    <span
                      id="stat-comments-corrected-spelling-label"
                      className={style['settings-dropdown-option-label']}
                    >
                      Corrected spelling
                    </span>
                  </div>
                </div>
              )}
              {selectedQuestion == DEFAULT_SELECTED_QUESTION && hasQuestionOrder ? (
                <div role="radiogroup" aria-label="Question order">
                  <div
                    data-testid="stat-comments-qs-survey-order"
                    className={classNames(style['settings-dropdown-option'], {
                      [style['settings-dropdown-option-disabled']]: !hasQuestionOrder
                    })}
                    onClick={hasQuestionOrder ? () => onToggleResponseOrder(false) : null}
                  >
                    <RadioButton
                      checked={!isResponseOrder}
                      name="stat-comments-question-order"
                      aria-labelledby="stat-comments-survey-order-label"
                    />
                    <span id="stat-comments-survey-order-label" className={style['settings-dropdown-option-label']}>
                      Qs in survey order
                    </span>
                  </div>
                  <div
                    data-testid="stat-comments-qs-response-order"
                    className={classNames(style['settings-dropdown-option'], {
                      [style['settings-dropdown-option-disabled']]: !hasQuestionOrder
                    })}
                    onClick={hasQuestionOrder ? () => onToggleResponseOrder(true) : null}
                  >
                    <RadioButton
                      checked={isResponseOrder}
                      name="stat-comments-question-order"
                      aria-labelledby="stat-comments-response-order-label"
                    />
                    <span id="stat-comments-response-order-label" className={style['settings-dropdown-option-label']}>
                      Qs in response order
                    </span>
                  </div>
                </div>
              ) : null}
              <div
                data-testid="stat-comments-highlight-terms"
                className={style['settings-dropdown-option']}
                onClick={onToggleHighlightSearchTerms}
              >
                <Checkbox
                  checked={Boolean(isHighlightSearchTerms)}
                  aria-labelledby="stat-comments-highlight-terms-label"
                />
                <span id="stat-comments-highlight-terms-label" className={style['settings-dropdown-option-label']}>
                  Highlight search terms
                </span>
              </div>
              {isSentimentsManagementDisabled ? null : (
                <div
                  data-testid="stat-comments-show-sentiments"
                  className={style['settings-dropdown-option']}
                  onClick={onToggleShowSentiment}
                >
                  <Checkbox checked={Boolean(isShowSentiments)} aria-labelledby="stat-comments-show-sentiments-label" />
                  <span id="stat-comments-show-sentiments-label" className={style['settings-dropdown-option-label']}>
                    Show sentiment
                  </span>
                </div>
              )}
              {(null === question || !['ir', 'va', 'vr'].includes(question.type)) && !isAdvancedFiltersDisabled ? (
                <div
                  data-testid="stat-comments-create-filter"
                  className={classNames(style['settings-dropdown-option'], {
                    [style['settings-dropdown-option-disabled']]: isCreateFilterDisabled
                  })}
                  {...pressable(onNewFilter, { disabled: isCreateFilterDisabled })}
                >
                  <i className="icon-aytmfd-filter-v2" />
                  <span className={style['settings-dropdown-option-label']}>Create filter</span>
                </div>
              ) : null}
              {!isQAModeDisabled && !isVideoResponsesMode ? (
                <div
                  data-testid="stat-comments-qa-mode"
                  className={style['settings-dropdown-option']}
                  {...pressable(onToggleQAMode)}
                >
                  <i className="icon-aytmfd-qa-mode" />
                  <span className={style['settings-dropdown-option-label']}>Enter QA mode</span>
                </div>
              ) : null}
              <div className={style['settings-dropdown-footer']}>
                <span className={style['visually-hidden']}>
                  {`${totalComments || 0} text, ${totalImageResponses || 0} image and ${
                    totalVideoResponses || 0
                  } video responses match the current question scope and search`}
                </span>
                <span aria-hidden="true">
                  {totalComments ? (
                    <>
                      <i className="icon-aytmfd-comments-v2" />
                      {totalComments}
                    </>
                  ) : null}
                </span>
                <span aria-hidden="true">
                  {totalImageResponses ? (
                    <>
                      <i className="icon-aytmfd-image-responses-v2" />
                      {totalImageResponses}
                    </>
                  ) : null}
                </span>
                <span aria-hidden="true">
                  {totalVideoResponses ? (
                    <>
                      <i className="icon-aytmfd-video-responses-v2" />
                      {totalVideoResponses}
                    </>
                  ) : null}
                </span>
              </div>
            </div>
          </ClickOutside>
        )}
      </Dropdown>
    )
  }
)

SurveyDropdown.propTypes = {
  questionsList: PropTypes.array,
  responseOrderRef: PropTypes.object,
  opened: PropTypes.bool,
  hasQuestionOrder: PropTypes.bool,
  isAdvancedFiltersDisabled: PropTypes.bool,
  onNewFilter: PropTypes.func,
  onToggleQAMode: PropTypes.func,
  onClickOutside: PropTypes.func
}

SurveyDropdown.displayName = 'SurveyDropdown'

export default SurveyDropdown
